import { NumberUtil } from '@/util'
import {
    TableContainer, Paper, Table, TableCell, TableHead, TableRow, TableBody, TablePagination, Button, Select, MenuItem
} from '@/components'
import { CSSProperties, ChangeEvent, useState } from 'react'
import { BaseProps } from '@/components/types'
import { SourceData } from '@/components/control/TableSelectBox'
import { ColumnDef } from '../DataGrid/types'
import { DataGridButtonCell } from '../DataGrid/components'

function DataTable ( props: DataTableProps ) {
    const {
        columnDefs = [],
        rowData,
        tableMinWidth = 650,
        noRowNo = false,
    } = props
    const [ page, setPage ] = useState( 0 )
    const [ rowsPerPage, setRowsPerPage ] = useState( 10 )
    const [ colDefs ] = useState( !noRowNo ? [ { headerName: 'No.', field: 'seq' }, ...columnDefs ] : columnDefs )
    const emptyRows = page > 0 ? Math.max( 0, ( 1 + page ) * rowsPerPage - rowData.length ) : 0

    const onChangeRowsPerPage = ( e: ChangeEvent<HTMLInputElement> ) => {
        setRowsPerPage( Number( e.target.value ) )
        setPage( 0 )
    }

    const getSequenceTableCell = ( rowData: Record<string, any>[], row: Record<string, any>, key: string ): JSX.Element => {
        const rowIdx = rowData.indexOf( row )
        return <TableCell
            key={ key }
            align="center"
            sx={ { width: 30 } }
        >
            { rowIdx + 1 }
        </TableCell>
    }

    const getButtonTypeTableCell = ( columnDef: ColumnDef, row: Record<string, any>, key: string, style: CSSProperties ): JSX.Element => {
        const { buttonProps } = columnDef || {}

        return <td style={ { borderBottom: '1px solid rgba(224, 224, 224, 1)' } } key={ key }>
            <DataGridButtonCell
                data={ row }
                buttonProps={ buttonProps }
            />
        </td>
    }

    const getSelectTypeTableCell = ( columnDef: ColumnDef, value: string, key: string, style: CSSProperties ): JSX.Element => {
        const { selectProps: {
            dataSource,
            onChange,
        } }= columnDef || {}
        const [ selectValue, setSelectValue ] = useState( value )

        const onCustomChange = ( value ): void => {
            setSelectValue( value )
            onChange && onChange( value )
        }

        return (
            dataSource && dataSource.length !== 0 && (
                <td style={ style } key={ key }>
                    <Select
                        fullWidth
                        value={ selectValue }
                        onChange={ ( e ) =>  onCustomChange( e?.target?.value ) }
                    >
                        {
                            dataSource && dataSource.map( (
                                { value, name }
                            ) => <MenuItem
                                key={ `${ value }${ name }` }
                                value={ value }
                            >
                                { name }
                            </MenuItem> )
                        }
                    </Select>
                </td>
            )
        )
    }

    const getDefaultTypeTableCell = ( value: string, idx: number, columnDef: ColumnDef, key: string ) => {
        const { textAlign, width, minWidth, format } = columnDef
        if(typeof value === 'object') value = ''
        return <TableCell
            key={ key }
            align={ textAlign }
            sx={ { width, minWidth } }
        >
            { format && typeof value === 'number' ? format( value ) : value }
        </TableCell>
    }

    return (
        <Paper className="w100p">
            <TableContainer component={ Paper }>
                <Table stickyHeader sx={ { minWidth: tableMinWidth } }>
                    <TableHead>
                        <TableRow>
                            {
                                colDefs.map( ( { headerName = '' }, idx ) =>
                                    <TableCell
                                        key={ `${ headerName }${ idx }` }
                                        align="center"
                                        sx={ { backgroundColor: '#f4f4f4' } }
                                    >
                                        { headerName }
                                    </TableCell>
                                )
                            }
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {
                            rowData && rowData.slice( page * rowsPerPage, page * rowsPerPage + rowsPerPage ).map( ( row, rowIdx ) =>
                                <TableRow hover key={ `${ row }${ rowIdx }` } className="flex">
                                    {
                                        colDefs.map( ( columnDef, colIdx ) => {
                                            const { field, textAlign, width, minWidth, type, format } = columnDef
                                            const value = row[ field ] || {}
                                            const key = `${ colIdx }${ rowIdx }`

                                            if ( colIdx === 0 && !noRowNo ) return getSequenceTableCell( rowData, row, key )
                                            if ( type === 'button' ) return getButtonTypeTableCell( columnDef, row, key, { width, minWidth } )
                                            if ( type === 'select' ) return getSelectTypeTableCell( columnDef, value, key, { width, minWidth } )

                                            return getDefaultTypeTableCell( value, colIdx, {
                                                field, textAlign, width, minWidth, format
                                            }, key )

                                        } )
                                    }
                                </TableRow>
                            )
                        }
                        {
                            emptyRows > 0 && (
                                <TableRow style={{ height: 53 * emptyRows }}>
                                    <TableCell colSpan={6} />
                                </TableRow>
                            )
                        }
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                rowsPerPageOptions={ [ 10, 25, 100 ] }
                count={ rowData.length }
                rowsPerPage={ rowsPerPage }
                page={ page }
                onPageChange={ ( e, page ) => setPage( page ) }
                onRowsPerPageChange={ onChangeRowsPerPage }
            />
        </Paper>
    )
}

export default DataTable

export interface ButtonTypeDataProps {
    variant?: 'text' | 'outlined' | 'contained'
    text?: string
    onClick?: ( row: Record<string, any> ) => void
}

export interface SelectTypeDataProps {
    value?: string,
    dataSource?: SourceData[]
    onChange?: ( value: any ) => void
}
export interface DataTableProps extends BaseProps {
    columnDefs?: ColumnDef[]
    rowData: Record<string, any>[]
    tableMinWidth?: number
    noRowNo?: boolean
}